import { useEffect, useState } from "react";
import NavBar from "../../component/navBar/NavBar";
import { useAuth } from "../../config/utilities/useAuth";
import Title from "../../component/title/Title";
import Table from "../../component/table/Table";
import DeleteModal from "../../component/modal/DeleteModal";
import { StopForm } from "./StopForm";
import SnackBar from "../../component/Snackbar/Snackbar";
import {
  APICreateStop,
  APIDeleteStops,
  APIGetStops,
  APIUpdateStop,
} from "../../config/utilities/APIs/stopsApi";
import DeactivationModal from "../../component/modal/DeactivationModal";
import "./Stops.css";

const headers = [
  { key: "name", name: "Stop Name", sortable: false },
  { key: "address", name: "Stop Address", sortable: false },
  { key: "totalChildCount", name: "Associated Students", sortable: false },
  { key: "status", name: "Status", sortable: false },
];

const statusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

export default function Stops() {
  const { privilege, token } = useAuth();
  const role = privilege?.role;
  const institutionId = privilege?.institutionId;
  const [action, setAction] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarType, setSnackbarType] = useState("");
  const [snackbarTitle, setSnackbarTitle] = useState("");
  const [snackbarSubtitle, setSnackbarSubtitle] = useState("");
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [fetchedStopData, setFetchedStopData] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedFilters, setSelectedFilters] = useState({
    status: "",
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [initialInput, setInitialInput] = useState(null);
  const [showDeactivationModal, setShowDeactivationModal] = useState(false);

  const filters = [
    { name: "status", placeholder: "Filter by Status", options: statusOptions },
  ];

  const [input, setInput] = useState({
    name: "",
    type: "",
    description: "",
    address: "",
    location: {
      type: "Point",
      coordinates: [],
    },
    associatedChilds: [],
    status: "active",
    updateScope: "none",
  });

  const [error, setError] = useState({
    name: false,
    type: false,
    description: false,
    address: false,
    location: false,
    associatedChilds: false,
  });

  const fetchStops = async (
    page = 1,
    filters = selectedFilters,
    searchTerm
  ) => {
    setLoading(true);

    try {
      const filterParams = Object.fromEntries(
        Object.entries(filters).filter(([_, v]) => v !== "")
      );
      const response = await APIGetStops(
        page,
        filterParams,
        setLoading,
        institutionId,
        searchTerm
      );

      if (response.status === 200) {
        const { data } = response.data;
        setFetchedStopData(data);
        setPageNumber(page);
      } else if (response.status === 400) {
        console.warn("Bad Request:", response);
      } else if (response.status === 401) {
        console.warn("Unauthorized:", response);
      } else if (response.status === 403) {
        console.warn("Forbidden:", response);
      } else if (response.status === 404) {
        console.warn("Not Found:", response);
      } else if (response.status >= 500) {
        console.error("Server Error:", response);
      } else {
        console.warn("Unexpected Status Code:", response.status, response);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStops();
  }, []);

  useEffect(() => {
    fetchStops(1, selectedFilters, searchTerm);
  }, [selectedFilters, searchTerm]);

  const handleReset = async () => {
    setInput({
      name: "",
      type: "",
      description: "",
      address: "",
      location: {
        type: "Point",
        coordinates: [],
      },
      associatedChilds: [],
      status: "active",
      updateScope: "none",
    });
    setError({
      name: false,
      type: false,
      description: false,
      address: false,
      location: false,
      associatedChilds: false,
    });
    setFetchedStopData([]);
    setShowDeleteModal(false);
    setShowDeactivationModal(false);
    setSelectedRowData(null);
    setAction(null);
    setPageNumber(1);
    await fetchStops(1, selectedFilters, searchTerm);
  };

  const handleDelete = async (row) => {
    setLoading(true);

    const rowId = row._id;

    try {
      const response = await APIDeleteStops(
        rowId,
        setLoading,
        institutionId,
        token
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Delete Successful");
        setSnackbarSubtitle("Stop has been deleted successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to delete stop"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Delete Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleEdit = async () => {
    setLoading(true);

    const formattedInput = formatInput(input, initialInput);

    try {
      const response = await APIUpdateStop(
        formattedInput,
        setLoading,
        institutionId,
        token
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Update Successful");
        setSnackbarSubtitle("Stop has been updated successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to update stop"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Update Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleCreate = async () => {
    setLoading(true);

    const formattedInput = formatInput(input, initialInput);

    try {
      const response = await APICreateStop(
        formattedInput,
        setLoading,
        institutionId,
        token
      );

      if (response?.status === 200) {
        setShowSnackbar(true);
        setSnackbarTitle("Create Successful");
        setSnackbarSubtitle("Stop has been created successfuly");
        setSnackbarType("success");
      } else {
        throw new Error(
          response?.response?.data?.message || "Failed to create stop"
        );
      }
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle("Create Failed");
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handleStatus = async (action) => {
    setLoading(true);
    let status;
    let title;
    if (action === "activate") {
      status = "active";
      title = "Activation";
    } else {
      status = "inactive";
      title = "Deactivation";
    }

    const payload = { status, _id: selectedRowData._id };
    try {
      await APIUpdateStop(payload, setLoading, institutionId, token);
    } catch (error) {
      setShowSnackbar(true);
      setSnackbarTitle(`${title} Failed`);
      setSnackbarSubtitle(error.message || "An error occurred");
      setSnackbarType("error");
    } finally {
      handleReset();
    }
  };

  const handlePopoverAction = (action, rowId) => {
    const selectedRow = fetchedStopData.stops.find((row) => row._id === rowId);
    if (action === "edit") {
      setSelectedRowData(selectedRow);
      setAction("edit");
    } else if (action === "delete") {
      setSelectedRowData(selectedRow);
      setShowDeleteModal(true);
    } else if (action === "activate") {
      setSelectedRowData(selectedRow);
      setAction("activate");
      setShowDeactivationModal(true);
    } else if (action === "deactivate") {
      setSelectedRowData(selectedRow);
      setAction("deactivate");
      setShowDeactivationModal(true);
    } else {
      setSelectedRowData(selectedRow);
      setAction("view");
    }
  };

  const handleOpenCreate = () => {
    setAction("create");
  };

  const formatInput = (rawInput, initialInput) => {
    const formattedCurrent = {
      ...(action === "edit" ? { _id: rawInput._id } : {}),
      name: rawInput.name.trim(),
      description: rawInput.description.trim(),
      type: rawInput.type.trim(),
      address: rawInput.address,
      status: rawInput.status,
      location: {
        type: "Point",
        coordinates: [
          rawInput.location.coordinates[0],
          rawInput.location.coordinates[1],
        ],
      },
      associatedChilds: rawInput.associatedChilds.map((child) => ({
        childId: child.id,
        tripSession: child.tripSession || "none",
      })),
    };

    if (action === "create") {
      return formattedCurrent;
    }

    const formattedInitial = {
      _id: initialInput._id,
      name: initialInput.name.trim(),
      description: initialInput.description.trim(),
      type: initialInput.type.trim(),
      address: initialInput.address,
      status: initialInput.status,
      location: {
        type: "Point",
        coordinates: [
          initialInput.location.coordinates[0],
          initialInput.location.coordinates[1],
        ],
      },
      associatedChilds: initialInput.associatedChilds.map((child) => ({
        childId: child.id,
        tripSession: child.tripSession || "none",
      })),
    };

    let changes = getChangedFields(formattedInitial, formattedCurrent);
    changes._id = rawInput._id;
    changes.updateScope = rawInput.updateScope;

    return changes;
  };

  const getChangedFields = (initial, current) => {
    const changes = {};

    for (const key in current) {
      if (typeof current[key] === "object" && current[key] !== null) {
        if (Array.isArray(current[key])) {
          if (JSON.stringify(current[key]) !== JSON.stringify(initial[key])) {
            changes[key] = current[key];
          }
        } else {
          const nestedChanges = getChangedFields(
            initial[key] || {},
            current[key]
          );
          if (Object.keys(nestedChanges).length > 0) {
            changes[key] = current[key];
          }
        }
      } else {
        if (current[key] !== initial[key]) {
          changes[key] = current[key];
        }
      }
    }

    return changes;
  };

  const renderTitle = () => {
    switch (action) {
      case "edit":
        return (
          <Title
            title="Edit Stop"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "create":
        return (
          <Title
            title="Create Stop"
            titleSize="1.5rem"
            gap="0.5rem"
            lineHeight="2.5rem"
          />
        );
      case "view":
        <Title
          title="View Stop"
          titleSize="1.5rem"
          gap="0.5rem"
          lineHeight="2.5rem"
        />;
      default:
        return (
          <Title
            title="Stop Management"
            subtitle="Manage all the stops in your institution"
            titleSize="1.5rem"
            subtitleSize="1rem"
            gap="0.5rem"
            lineHeight="2rem"
          />
        );
    }
  };

  const renderContent = () => {
    switch (action) {
      case "edit":
        return (
          <StopForm
            mode="edit"
            existingData={selectedRowData}
            onSubmit={handleEdit}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            role={role}
            initialInput={initialInput}
            setInitialInput={setInitialInput}
          />
        );
      case "create":
        return (
          <StopForm
            mode="create"
            onSubmit={handleCreate}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            initialInput={initialInput}
            setInitialInput={setInitialInput}
          />
        );
      case "view":
        return (
          <StopForm
            mode="view"
            existingData={selectedRowData}
            handleReset={handleReset}
            loading={loading}
            setLoading={setLoading}
            input={input}
            setInput={setInput}
            error={error}
            setError={setError}
            initialInput={initialInput}
            setInitialInput={setInitialInput}
          />
        );
      default:
        return (
          <Table
            data={fetchedStopData?.stops || []}
            searchText="Search by stop or associated student..."
            headers={headers}
            showEmptyHeader={true}
            showFilter={true}
            handlePopoverAction={handlePopoverAction}
            onClick={handleOpenCreate}
            module="stops"
            loading={loading}
            showTableAction={
              role === "ADMIN" || role === "SUPERADMIN" || role === "EDITOR"
            }
            height="75vh"
            useBackendPagination={true}
            onPageChange={fetchStops}
            totalPages={fetchedStopData?.pagination?.totalPages || 1}
            currentPage={pageNumber}
            filters={filters}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            totalRecords={fetchedStopData?.pagination?.total || 0}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
          />
        );
    }
  };

  return (
    <div className="main-page stops-page">
      <NavBar type="stops" action={action} handleReset={handleReset} />
      <div className="content-area stops-content-area">
        <div className="title-container mobile-hide-page-title">{renderTitle()}</div>
        <div className="main-content-container stops-main-content-container">{renderContent()}</div>
      </div>
      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onDelete={handleDelete}
        itemName={selectedRowData}
        module="stops"
        loading={loading}
      />
      <DeactivationModal
        isOpen={showDeactivationModal}
        onClose={() => setShowDeactivationModal(false)}
        loading={loading}
        onToggle={handleStatus}
        itemName={selectedRowData}
        module="stops"
        action={action}
      />
      {showSnackbar && (
        <div className="notification">
          <SnackBar
            state={showSnackbar}
            setState={setShowSnackbar}
            title={snackbarTitle}
            subtitle={snackbarSubtitle}
            module={snackbarType}
            action={snackbarType}
          />
        </div>
      )}
    </div>
  );
}
